import { useState, useLayoutEffect, useEffect } from 'react';
import { getTrackById } from '../../assets/asset_loaders/tracks';
import Map from '../../components/Map/Map';
import Cup from '../../components/Cup';
import HighwaysSidebar from '../../components/HighwaysSidebar/HighwaysSidebar';
import CupBuilderHeader from '../../../shared/components/CupBuilderHeader/CupBuilderHeader';
import CupComplete from '../../../shared/components/CupComplete/CupComplete';
import SmallPop from '../../../shared/components/pops/SmallPop';
import cupToString from '../../functions/cupToString';
import './WorldCupBuilder.css';

function WorldCupBuilder() {

  const [selectedTrack, setSelectedTrack] = useState(null);
  const [highlightedConnectionId, setHighlightedConnectionId] = useState(null);
  const [cup, setCup] = useState([null, null, null, null]);
  const [smallPop, setSmallPop] = useState(null);

  useEffect(() => {
    const modal = document.querySelector('.track-modal');
    if (!modal) return;

    const findScrollableAncestor = (element) => {
      let current = element;
      while (current) {
        const style = getComputedStyle(current);
        const overflowY = style.overflowY;
        const isScrollable = ['auto', 'scroll', 'overlay'].includes(overflowY)
          && current.scrollHeight > current.clientHeight;
        if (isScrollable) {
          return { element: current, overflowY, position: style.position };
        }
        current = current.parentElement;
      }
      const docStyle = getComputedStyle(document.documentElement);
      return { element: document.documentElement, overflowY: docStyle.overflowY, position: docStyle.position };
    };

    const scrollContainer = findScrollableAncestor(modal);
    console.log('WorldCupBuilder scroll container', {
      tag: scrollContainer.element.tagName,
      class: scrollContainer.element.className,
      id: scrollContainer.element.id,
      overflowY: scrollContainer.overflowY,
      position: scrollContainer.position
    });
  }, []);

  useLayoutEffect(() => {
    if (smallPop) {
      const timeout = setTimeout(() => {
        setSmallPop(null);
      }, 3500);
      return () => clearTimeout(timeout);
    }
  }, [smallPop]);

  const handleTrackClick = (trackId) => {
    const originalTrack = getTrackById(trackId);
    const track = {
      ...originalTrack,
      connections: [originalTrack.id, ...originalTrack.connections]
    };

    setSelectedTrack(track);
    setHighlightedConnectionId(null);

    const sidebar = document.querySelector('.highways-sidebar');
    if (sidebar) {
      sidebar.scrollTop = 0;
    }
  };

  const handleConnectionHover = (connectionId) => {
    setHighlightedConnectionId(connectionId);
  };

  const handleConnectionLeave = () => {
    setHighlightedConnectionId(null);
  };

  const handleResetSelection = () => {
    setSelectedTrack(null);
    const resetButton = document.querySelector('.map-reset-zoom');
    if (resetButton) {
      resetButton.click();
    }
  };

  const addTrackToCup = (track) => {
    const newCup = [...cup];
    const connectedTrack = getTrackById(track);

    let end;
    if (selectedTrack.id === connectedTrack.id) {
      end = null;
    } else {
      end = connectedTrack.id;
    }

    newCup[newCup.indexOf(null)] = { start: selectedTrack.id, end: end };
    setCup(newCup);

    if (newCup.indexOf(null) !== -1) {
      handleTrackClick(track);
      setHighlightedConnectionId(null);
    }
  }

  const handleUndoCup = () => {
    const newCup = [...cup];
    const lastFilledIndex = newCup.map((track, idx) => track !== null ? idx : -1).filter(idx => idx !== -1).pop();

    if (lastFilledIndex !== undefined && lastFilledIndex >= 0) {
      const previousTrack = newCup[lastFilledIndex];
      newCup[lastFilledIndex] = null;
      setCup(newCup);

      if (previousTrack && previousTrack.start) {
        handleTrackClick(previousTrack.start);
      }
    }
  };

  const handleResetCup = () => {
    setCup([null, null, null, null]);
    setSelectedTrack(null);
    setHighlightedConnectionId(null);
    handleResetSelection();
  };

  const getCurrentTrackNumber = () => {
    const nextNullIndex = cup.indexOf(null);
    return nextNullIndex === -1 ? 4 : nextNullIndex + 1;
  };

  /**
   * Formats cup data for Firestore storage.
   */
  const formatCupForFirestore = (cupData, cupName) => {
    // Format tracks for Firestore storage
    const tracks = cupData.map((track) => ({
      start: track.start,
      end: track.end
    }));

    // Create track names list for querying (full representation)
    const trackList = cupData.map((track) => {
      const startTrack = getTrackById(track.start);
      const endTrack = track.end ? getTrackById(track.end) : null;

      if (endTrack && startTrack.id !== endTrack.id) {
        return `${startTrack.name} → ${endTrack.name}`;
      } else {
        return startTrack.name;
      }
    });

    // Create ending location list for browse filters (primary filter)
    // When end is null, the race is at the start track (full race)
    const trackEndList = cupData.map((track) => {
      const endLocation = track.end || track.start;
      const endTrack = getTrackById(endLocation);
      return endTrack.name;
    });

    return {
      tracks: tracks,
      trackList: trackList,
      trackEndList: trackEndList
    };
  };

  return (
    <div id="main-container">
      {cup.indexOf(null) >= 0 ? (
        // Track selection mode
        <div className='track-modal'>
          <CupBuilderHeader
            onUndo={handleUndoCup}
            onReset={handleResetCup}
            currentTrackNumber={getCurrentTrackNumber()}
            isDisabled={cup.every(track => track === null)}
            variant="world"
          />

          <Cup tracks={cup} />


          <div className="app-container">
            <Map
              clickTrackIcon={handleTrackClick}
              selectedTrack={selectedTrack}
              highlightedConnectionId={highlightedConnectionId}
              onResetZoom={() => setSelectedTrack(null)}
            />

            <HighwaysSidebar
              selectedTrack={selectedTrack}
              highlightedConnectionId={highlightedConnectionId}
              onTrackHover={handleConnectionHover}
              onTrackLeave={handleConnectionLeave}
              onTrackClick={addTrackToCup}
              onReset={handleResetSelection}
            />
          </div>
        </div>
      ) : (
        // Cup complete mode
        <div className='track-modal'>
          <CupComplete
            cup={cup}
            cupDisplay={() => <Cup tracks={cup} />}
            onReset={handleResetCup}
            formatCupForFirestore={formatCupForFirestore}
            formatCupText={cupToString}
            firestoreCollection="worldCups"
            userCupsField="worldCups"
            gameLabel="Mario Kart World"
            shareUrl="https://mk8dx-cup-builder.web.app/"
            defaultImageName="Your World Cup"
            signInState={{ worldCup: cup }}
            variant="world"
          />
        </div>
      )}

      {smallPop && (
        <SmallPop
          text={smallPop.text}
          link={smallPop.link}
          onClose={() => setSmallPop(null)}
        />
      )}
    </div>
  );
}

export default WorldCupBuilder;
